'use client';

import cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useRef } from 'react';

import { PAGE_NAMES } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import type { Locale } from '~/universal/constants';

type AuthSessionSyncProps = Readonly<{
  lng: Locale;
  userId?: string | null;
}>;

type DecodedAuthToken = Readonly<{
  _id?: string;
}>;

const getCookieUserId = (): string | null => {
  const token = cookies.get('id_token');

  if (!token) return null;

  try {
    const decodedToken = jwtDecode<DecodedAuthToken>(token);
    return decodedToken?._id ? String(decodedToken._id) : null;
  } catch {
    return null;
  }
};

const getOnBehalfUserId = (): string | null => {
  try {
    const sessionKey = window.localStorage.getItem('sessionKey');
    const currentUserId = window.localStorage.getItem('currentUser');

    return sessionKey && currentUserId ? String(currentUserId) : null;
  } catch {
    return null;
  }
};

const AuthSessionSync = ({ lng, userId }: AuthSessionSyncProps) => {
  const isRedirectingRef = useRef(false);

  useEffect(() => {
    if (!userId) return;

    const checkCurrentSession = () => {
      if (isRedirectingRef.current || document.visibilityState === 'hidden') return;

      const cookieUserId = getCookieUserId();
      const onBehalfUserId = getOnBehalfUserId();

      if (cookieUserId === String(userId) || cookieUserId === onBehalfUserId) return;

      isRedirectingRef.current = true;

      const signInUrl = new URL(getLocaleLink(PAGE_NAMES.SIGN_IN, lng), window.location.origin);
      signInUrl.searchParams.set('sessionSync', '1');
      window.location.replace(signInUrl.toString());
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkCurrentSession();
      }
    };

    checkCurrentSession();
    window.addEventListener('focus', checkCurrentSession);
    window.addEventListener('pageshow', checkCurrentSession);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', checkCurrentSession);
      window.removeEventListener('pageshow', checkCurrentSession);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [lng, userId]);

  return null;
};

export default AuthSessionSync;
